// Toast (DESIGN §5.12): um por vez, tinta, some em 4 s (6 s com ação ou `longo`), pausa ao focar/pairar.
// role="status" (sucesso) ou role="alert" (erro). Uso: const toast = useToast(); toast.sucesso("Salvo.")
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Icones } from "./icones";

export interface OpcoesToast {
  acao?: { rotulo: string; aoClicar: () => void };
  /** 6 s em vez de 4 s, para texto que o aluno precisa ler inteiro (ex.: treino descartado pela fila). */
  longo?: boolean;
  /**
   * No topo, logo abaixo da barra de status, em vez de acima da tab bar: para telas com o teclado aberto, onde o de
   * baixo ficaria atrás dele (código reenviado; docs/prototipo/senha-propria-aluno.html 4.5).
   */
  topo?: boolean;
}

interface ToastAtual extends OpcoesToast {
  id: number;
  tipo: "sucesso" | "erro" | "info";
  texto: string;
}

export interface ApiToast {
  sucesso(texto: string, opcoes?: OpcoesToast): void;
  erro(texto: string, opcoes?: OpcoesToast): void;
  info(texto: string, opcoes?: OpcoesToast): void;
  fechar(): void;
}

const ContextoToast = createContext<ApiToast | null>(null);

export function ProvedorToast({ children }: { children: ReactNode }) {
  const [atual, setAtual] = useState<ToastAtual | null>(null);
  const [pausado, setPausado] = useState(false);
  const contador = useRef(0);

  const mostrar = useCallback((tipo: ToastAtual["tipo"], texto: string, opcoes?: OpcoesToast) => {
    contador.current += 1;
    setAtual({ id: contador.current, tipo, texto, ...opcoes });
  }, []);

  const api = useMemo<ApiToast>(
    () => ({
      sucesso: (t, o) => mostrar("sucesso", t, o),
      erro: (t, o) => mostrar("erro", t, o),
      info: (t, o) => mostrar("info", t, o),
      fechar: () => setAtual(null),
    }),
    [mostrar],
  );

  useEffect(() => {
    if (!atual || pausado) return;
    const t = window.setTimeout(() => setAtual(null), atual.acao || atual.longo ? 6000 : 4000);
    return () => window.clearTimeout(t);
  }, [atual, pausado]);

  const Icone =
    atual?.tipo === "erro" ? Icones.erro : atual?.tipo === "info" ? Icones.info : Icones.confirmar;

  return (
    <ContextoToast.Provider value={api}>
      {children}
      {createPortal(
        <div
          className={
            atual?.topo
              ? "pointer-events-none fixed top-[calc(env(safe-area-inset-top)+8px)] right-margem left-margem z-60 flex justify-center"
              : "pointer-events-none fixed right-margem bottom-[calc(64px+env(safe-area-inset-bottom)+12px)] left-margem z-60 flex justify-center lg:right-6 lg:bottom-6 lg:left-auto lg:w-96"
          }
        >
          {atual && (
            <div
              key={atual.id}
              role={atual.tipo === "erro" ? "alert" : "status"}
              aria-live={atual.tipo === "erro" ? "assertive" : "polite"}
              onMouseEnter={() => setPausado(true)}
              onMouseLeave={() => setPausado(false)}
              onFocus={() => setPausado(true)}
              onBlur={() => setPausado(false)}
              className="pointer-events-auto flex min-h-12 w-full max-w-app items-center gap-3 rounded-controle bg-tinta px-4 py-3 text-apoio text-sobre-tinta shadow-toast animate-toast-entra"
            >
              <Icone size={20} aria-hidden className="shrink-0" />
              <span className="flex-1">{atual.texto}</span>
              {atual.acao && (
                <button
                  type="button"
                  // -my-3: o toque continua 48, e o toast com ação mantém os 48 de altura (bloquear-aluno.html, toast F)
                  className="-my-3 h-toque shrink-0 px-2 font-semibold underline underline-offset-4 foco-sobre-tinta"
                  onClick={() => {
                    atual.acao?.aoClicar();
                    setAtual(null);
                  }}
                >
                  {atual.acao.rotulo}
                </button>
              )}
            </div>
          )}
        </div>,
        document.body,
      )}
    </ContextoToast.Provider>
  );
}

export function useToast(): ApiToast {
  const api = useContext(ContextoToast);
  if (!api) throw new Error("useToast precisa de <ProvedorToast>");
  return api;
}
